<template>
  <v-dialog
    scrollable
    persistent
    :modelValue="priorityListDialog"
    maxWidth="800"
    @update:modelValue="v => (v ? show() : close())"
  >
    <v-card>
      <v-card-title class="headline">
        {{ capitalize(title) }}
      </v-card-title>
      <v-card-text class="promotion-priority-div">
        <div class="mb-10">
          Prompt that sits higher on the list will be given priority over the ones below.
        </div>

        <div class="list-group-header">
          <div class="promotion-priority-col-0">
            <span></span>
          </div>
          <div class="promotion-priority-col-1">
            <span>Position</span>
          </div>
          <div class="promotion-priority-col-2 flex justify-between">
            <span>Prompt Name</span>
            <v-checkbox
              v-model="removeEnded"
              class="mt-0 mr-0 pt-0"
              label="Active prompts only"
              dark
              density="compact"
              hideDetails
            ></v-checkbox>
          </div>
        </div>
        <template v-if="allPromptsStore.getPromptsLoading">
          <RfBaseSkeleton v-for="i in 5" :key="i" width="720" height="24" class="my-2 w-full" />
        </template>
        <draggable
          v-else
          v-model="prioritizedPaths"
          itemKey="id"
          :animation="150"
          :componentData="{ name: 'flip-list', tag: 'div', type: 'transition' }"
          v-bind="dragOptions"
          class="priority-list-wrapper"
          @start="isDragging = true"
          @end="isDragging = false"
        >
          <template #item="{ element, index }">
            <span
              v-if="!removeEnded || (element.is_enabled && !isPromptExpired(element.end_date))"
              :class="
                element.id === currRetention.id
                  ? 'current-retention list-group-item'
                  : 'list-group-item'
              "
            >
              <span class="promotion-priority-col-0">
                <v-icon size="small">md:drag_indicator</v-icon>
              </span>
              <span class="promotion-priority-col-1">
                <span>{{ index + 1 }}</span>
              </span>
              <span class="promotion-priority-col-2">
                <span @click="showRetention(element)">{{ element.name }}</span>
              </span>
              <span class="promotion-priority-col-3">
                <v-icon v-if="element.id === currRetention.id" color="primary" size="16"
                  >md:star</v-icon
                >
              </span>
            </span>
          </template>
        </draggable>
      </v-card-text>
      <v-card-actions class="modal-card-actions-div">
        <v-btn
          size="large"
          class="cancel-btn"
          variant="outlined"
          style="width: 100px"
          @click="close()"
          >Close</v-btn
        >
        <v-btn
          size="large"
          variant="flat"
          color="primary"
          style="width: 170px"
          @click="updatePriorityListClick"
        >
          <v-icon start>md:save</v-icon>Save changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import draggable from "vuedraggable";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { cloneDeep } from "lodash-es";
import StringUtils from "@/utils/StringUtils";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";

export default {
  name: "RfRetentionPriority",
  components: { draggable, RfBaseSkeleton },
  props: ["filterFn", "currRetention"],
  setup: () => ({ capitalize: StringUtils.capitalize, allPromptsStore: useAllPromptsStore() }),
  data: () => ({ currentRetentions: [], priorityListDialog: false, removeEnded: false }),
  computed: {
    prioritizedPaths: {
      get() {
        return this.removeEnded
          ? [...this.currentRetentions].sort((a, b) => {
              const aEnabled = a.is_enabled && !this.isPromptExpired(a.end_date);
              const bEnabled = b.is_enabled && !this.isPromptExpired(b.end_date);
              if (aEnabled === bEnabled) return 0;
              return aEnabled ? -1 : 1;
            })
          : this.currentRetentions;
      },
      set(value) {
        this.currentRetentions = value.map((item, index) => ({
          ...item,
          path_group: { ...item.path_group, priority: index },
        }));
      },
    },
    dragOptions() {
      return {
        animation: 0,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
      };
    },
    title() {
      if (!this.currRetention) return "";
      if (["video", "retention_modal"].indexOf(this.currRetention.path_type) > -1) {
        return "Popup and Video Priority";
      }
      return `${this.currRetention.path_type} Priority`;
    },
  },
  methods: {
    async show() {
      this.priorityListDialog = true;
      if (!this.allPromptsStore.prompts.length)
        await this.allPromptsStore.getPrompts(this.$route.params.aid, { nonBlocking: true });
      this.currentRetentions = (
        cloneDeep(this.allPromptsStore.prompts.filter(this.filterFn)) || []
      ).sort(
        (a, b) =>
          (a.path_group ? a.path_group.priority : 0) - (b.path_group ? b.path_group.priority : 0),
      );
    },
    close() {
      this.priorityListDialog = false;
    },
    showRetention(retention) {
      this.close();
      const appId = this.$route.params.aid;
      this.$router.push({ path: `/apps/${appId}/retentions/${retention.id}` });
    },
    async updatePriorityListClick() {
      const appId = this.$route.params.aid;
      const pathGroups = this.currentRetentions.map(item => item.path_group);
      this.allPromptsStore.setPriorityRetentions({ appId, pathGroups });
      this.close();
    },
    isPromptExpired: endDate => +new Date(endDate) < +new Date(),
  },
};
</script>
